/*
 * Figma `111:122` — SETUP-02 · Seat Plan · Zones + Spatial Canvas.
 *
 * Built against the half of that frame the product actually has, and deliberately not the other
 * half. The frame draws a free-placement canvas: a STAGE block and zone rectangles positioned by
 * dragging them around a room. `SeatZoneDto` carries no coordinates and no endpoint accepts any,
 * so a canvas here would be a drawing tool whose arrangement is thrown away on reload — the one
 * outcome worse than not having it. The gap is reported rather than faked; see the commit and the
 * resume record.
 *
 * What the product has instead is a generator — rows × seats, rendered immediately exactly as the
 * buyer will see it — and that is what the workspace shows, beside the zone list the frame asks
 * for.
 *
 * The lock warning is the frame's own lead (`111:249`) and the part worth having most: seats a
 * buyer already holds cannot be moved or removed, adding new ones is still fine, and stating both
 * halves is what stops an organizer from assuming the plan is frozen.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, ManagementTemplate, Select, StatusBadge, TextField,
} from '..';
import type { Destination } from '../destinations';
import type { SeatDto } from '../../api';
import type { SeatPlanState } from './useSeatPlan';
import { useSeatPlan } from './useSeatPlan';

const baht = (n: number) => `฿${n.toLocaleString('th-TH')}`;

export function SeatPlanScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: SeatPlanState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    zones, seats, occupancy, zoneStats, lockedSeats, activeZoneId, setActiveZoneId,
    eligibleTickets, noSeatedTypes, loading, loadError, error, busy,
    createZone, generateLayout, toggleSeatBlocked, updateZone,
  } = state;

  const [draft, setDraft] = useState({
    ticketId: '', code: '', label: '', color: '#E8543F', unitKind: 'seat' as 'seat' | 'table',
  });
  const [layout, setLayout] = useState({ rows: '10', seatsPerRow: '12', rowLabelStyle: 'alpha' as 'alpha' | 'numeric', aisleAfter: '' });

  const active = zoneStats.find((s) => s.zone.id === activeZoneId) ?? null;

  /*
   * The label is the one zone setting held as a draft: colour and selection mode commit on change,
   * but a name commits on blur so it is not saved once per keystroke. It re-seeds whenever the
   * open zone changes, or an edit would follow you to the next zone.
   */
  const [zoneLabel, setZoneLabel] = useState(active?.zone.label ?? '');
  useEffect(() => { setZoneLabel(active?.zone.label ?? ''); }, [active?.zone.id, active?.zone.label]);
  const noun = active?.isTable ? 'โต๊ะ' : 'ที่';
  const sellable = seats.filter((s) => s.state !== 'blocked');
  const taken = seats.filter((s) => s.state === 'taken');
  const blocked = seats.filter((s) => s.state === 'blocked');
  const columns = seats.length ? Math.max(...seats.map((s) => s.x)) + 1 : 0;

  const seatLabel = (s: SeatDto) =>
    `${s.seatCode} — ${s.state === 'blocked' ? 'ปิดการขาย' : s.state === 'taken' ? 'ถูกจองแล้ว' : 'ว่าง'}`;

  return (
    <ManagementTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      summary={
        <>
          {error && <Alert tone="danger" title="ดำเนินการไม่สำเร็จ">{error}</Alert>}
          {lockedSeats > 0 && (
            <Alert tone="warning" title={`${lockedSeats.toLocaleString('th-TH')} ที่นั่งขายแล้วจะถูกล็อก`}>
              ที่นั่งที่มีผู้ซื้อแล้วย้ายโซนหรือลบไม่ได้ · การเพิ่มที่นั่งใหม่และการปิดขายที่นั่งที่ยังว่างยังทำได้ตามปกติ
            </Alert>
          )}
          {noSeatedTypes && (
            <Alert tone="info" title="ยังไม่มีประเภทบัตรที่ระบุที่นั่ง">
              ไปที่ “บัตรและราคา” แล้วตั้งรูปแบบที่นั่งของบัตรเป็น “ระบุที่นั่งรายตัวผ่านโซน” ก่อน — บัตรแบบระบุที่นั่งต้องเป็น 1 ใบ ต่อ 1 คน และขายหน้างานไม่ได้
            </Alert>
          )}
        </>
      }
      items={
        <div className="jt-registry-split">
          <Card
            title={`โซน · ${zones.length}`}
            sub="โซนไม่มีราคาของตัวเอง — ราคามาจากประเภทบัตรที่ผูกไว้"
          >
            {zones.length === 0 && <p className="jt-field-hint">ยังไม่มีโซน — สร้างทางขวา</p>}
            <ul className="jt-account-list">
              {zoneStats.map((s) => (
                <li key={s.zone.id}>
                  <button
                    type="button"
                    className="jt-account"
                    data-selected={s.zone.id === activeZoneId || undefined}
                    aria-current={s.zone.id === activeZoneId ? 'true' : undefined}
                    onClick={() => setActiveZoneId(s.zone.id)}
                  >
                    <span className="jt-account-title">
                      <span className="jt-zone-dot" style={{ background: s.zone.color }} aria-hidden="true" />
                      {s.zone.code} · {s.zone.label || s.ticket?.nameEn || '—'}
                    </span>
                    <span className="jt-account-sub">
                      {baht(s.price)} · {s.capacity.toLocaleString('th-TH')} {s.isTable ? 'โต๊ะ' : 'ที่'}
                      {s.isTable && ` · จุได้ ${s.admits.toLocaleString('th-TH')} คน`}
                      {' · ขายแล้ว '}{s.sold.toLocaleString('th-TH')}
                    </span>
                    <span className="jt-account-tags">
                      <StatusBadge tone={s.isTable ? 'accent' : 'neutral'}>
                        {s.isTable ? 'ขายเป็นโต๊ะ' : 'ขายรายที่นั่ง'}
                      </StatusBadge>
                      {s.sold > 0 && <StatusBadge tone="warning">{`ล็อก ${s.sold}`}</StatusBadge>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <div className="jt-sectionpanel">
            {active ? (
              <>
                <Card
                  title={`ผัง${active.isTable ? 'โต๊ะ' : 'ที่นั่ง'} · ${active.zone.code}`}
                  sub={`คลิกเพื่อปิดหรือเปิดการขายราย${noun} — ${noun}ที่ถูกจองแล้วกดไม่ได้`}
                >
                  <p className="jt-field-hint">
                    {active.isTable && <strong>{seats.length} โต๊ะ · จุได้ {sellable.length * (active.ticket?.seats ?? 1)} คน · </strong>}
                    ขายได้ {sellable.length} {noun} · ถูกจองแล้ว {taken.length} {noun} · ปิดขาย {blocked.length} {noun}
                  </p>
                  {seats.length === 0 ? (
                    <p className="jt-field-hint">ยังไม่มีผังในโซนนี้ — สร้างด้วยตัวสร้างผังด้านล่าง</p>
                  ) : (
                    <div className="jt-seatmap-scroll">
                      <div className="jt-seatmap" style={{ gridTemplateColumns: `repeat(${columns}, 26px)` }}>
                        {seats.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            className="jt-seat"
                            data-state={s.state}
                            title={seatLabel(s)}
                            aria-label={seatLabel(s)}
                            disabled={busy || s.state === 'taken'}
                            style={{
                              gridColumn: s.x + 1,
                              gridRow: s.y + 1,
                              background: s.state === 'available' ? active.zone.color : undefined,
                            }}
                            onClick={() => void toggleSeatBlocked(s)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                {/*
                  * A zone's own settings, which the rebuilt screen could not change at all: a zone
                  * was whatever it was created as. `selectionMode` is the one that changes what a
                  * buyer does, so it says what each choice means rather than naming the modes.
                  */}
                <Card
                  title={`ตั้งค่าโซน ${active.zone.code}`}
                  sub="มีผลกับผู้ซื้อทันที — ไม่กระทบที่นั่งที่ขายไปแล้ว"
                >
                  <div className="jt-field-grid">
                    <TextField
                      label="ชื่อโซนที่ผู้ซื้อเห็น"
                      value={zoneLabel}
                      disabled={busy}
                      onChange={(e) => setZoneLabel(e.target.value)}
                      onBlur={() => {
                        const next = zoneLabel.trim();
                        if (next && next !== active.zone.label) void updateZone(active.zone, { label: next });
                      }}
                      hint="บันทึกเมื่อออกจากช่อง"
                    />
                    <TextField
                      label="สีโซน"
                      type="color"
                      value={active.zone.color}
                      disabled={busy}
                      onChange={(e) => void updateZone(active.zone, { color: e.target.value })}
                      hint="ใช้บนผังที่ผู้ซื้อเห็น"
                    />
                  </div>
                  <Select
                    label="วิธีเลือกที่นั่ง"
                    value={active.zone.selectionMode}
                    disabled={busy}
                    options={[
                      { value: 'pick', label: 'ให้ผู้ซื้อเลือกเองบนผัง' },
                      { value: 'best_available', label: 'ระบบเลือกที่ว่างที่ดีที่สุดให้' },
                    ]}
                    onChange={(e) => void updateZone(active.zone, {
                      selectionMode: e.target.value as 'pick' | 'best_available',
                    })}
                  />
                </Card>

                <Card
                  title="ตัวสร้างผัง"
                  sub="แถว × ที่นั่งต่อแถว — สร้างใหม่จะแทนผังเดิมของโซนนี้"
                >
                  {active.sold > 0 && (
                    <Alert tone="danger" title={`โซนนี้มี ${active.sold} ที่ถูกขายไปแล้ว`}>
                      สร้างผังใหม่ทับจะกระทบที่นั่งที่มีเจ้าของอยู่ — ถ้าต้องเพิ่มที่นั่ง ให้สร้างโซนใหม่แทนการสร้างผังทับ
                    </Alert>
                  )}
                  <div className="jt-field-grid">
                    <TextField
                      label="จำนวนแถว" type="number" min={1} value={layout.rows}
                      onChange={(e) => setLayout({ ...layout, rows: e.target.value })}
                    />
                    <TextField
                      label={`${active.isTable ? 'โต๊ะ' : 'ที่นั่ง'}ต่อแถว`} type="number" min={1} value={layout.seatsPerRow}
                      onChange={(e) => setLayout({ ...layout, seatsPerRow: e.target.value })}
                    />
                  </div>
                  <div className="jt-field-grid">
                    <Select
                      label="รูปแบบชื่อแถว"
                      value={layout.rowLabelStyle}
                      options={[{ value: 'alpha', label: 'A, B, C…' }, { value: 'numeric', label: '1, 2, 3…' }]}
                      onChange={(e) => setLayout({ ...layout, rowLabelStyle: e.target.value as 'alpha' | 'numeric' })}
                    />
                    <TextField
                      label="เว้นทางเดินหลังตำแหน่งที่"
                      value={layout.aisleAfter}
                      placeholder="เช่น 4, 8"
                      onChange={(e) => setLayout({ ...layout, aisleAfter: e.target.value })}
                      hint="คั่นด้วยจุลภาค เว้นว่างได้ถ้าไม่มีทางเดิน"
                    />
                  </div>
                  <div className="jt-inline-actions">
                    <Button
                      variant="primary"
                      disabled={busy}
                      onClick={() => void generateLayout(active.zone.id, {
                        rows: Math.max(1, Number(layout.rows) || 1),
                        seatsPerRow: Math.max(1, Number(layout.seatsPerRow) || 1),
                        rowLabelStyle: layout.rowLabelStyle,
                        aisleAfter: layout.aisleAfter.split(',').map((v) => Number(v.trim())).filter((n) => n > 0),
                      })}
                    >
                      {busy ? 'กำลังสร้าง…' : 'สร้างผัง'}
                    </Button>
                  </div>
                </Card>
              </>
            ) : (
              <Card title="เลือกโซนเพื่อดูผัง" sub="หรือสร้างโซนแรกจากแผงด้านล่าง">
                <p className="jt-field-hint">แต่ละโซนมีผังของตัวเอง และผูกกับประเภทบัตรหนึ่งประเภท</p>
              </Card>
            )}
          </div>
        </div>
      }
      detail={
        <>
          {eligibleTickets.length > 0 && (
            <Card title="เพิ่มโซน" sub="โซนหนึ่งผูกกับประเภทบัตรหนึ่งประเภท และผูกซ้ำไม่ได้">
              <div className="jt-field-grid">
                <Select
                  label="ประเภทบัตร"
                  placeholder="— เลือก —"
                  value={draft.ticketId}
                  options={eligibleTickets.map((t) => ({ value: t.id, label: t.nameTh || t.nameEn }))}
                  onChange={(e) => setDraft({ ...draft, ticketId: e.target.value })}
                />
                <TextField
                  label="รหัสโซน"
                  requiredMark
                  value={draft.code}
                  maxLength={4}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                  hint="A–Z หรือ 0–9 ไม่เกิน 4 ตัว — ปรากฏในรหัสที่นั่งทุกที่ จึงเปลี่ยนภายหลังไม่ได้"
                />
                <TextField
                  label="ชื่อโซน"
                  value={draft.label}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
                <Select
                  label="หน่วยที่ขาย"
                  value={draft.unitKind}
                  options={[
                    { value: 'seat', label: 'ที่นั่ง — 1 ใบ ต่อ 1 คน' },
                    { value: 'table', label: 'โต๊ะ — 1 ใบ ต่อหลายคน ตามจำนวนในบัตร' },
                  ]}
                  onChange={(e) => setDraft({ ...draft, unitKind: e.target.value as 'seat' | 'table' })}
                />
              </div>
              <Alert tone="info" title="เลือกหน่วยให้ถูกตั้งแต่แรก">
                หน่วยที่ขายเปลี่ยนทีหลังไม่ได้ เพราะการเปลี่ยนจะเขียนรหัสที่นั่งทุกตัวในโซนใหม่
              </Alert>
              <div className="jt-inline-actions">
                <Button
                  variant="primary"
                  disabled={busy || !draft.ticketId || !draft.code.trim()}
                  onClick={async () => {
                    await createZone({
                      ticketId: draft.ticketId,
                      code: draft.code.trim(),
                      label: draft.label.trim(),
                      color: draft.color,
                      unitKind: draft.unitKind,
                    });
                    setDraft({ ...draft, ticketId: '', code: '', label: '' });
                  }}
                >
                  สร้างโซน
                </Button>
              </div>
            </Card>
          )}

          {occupancy.some((o) => o.orderCode) && (
            <Card title="ใครนั่งที่ไหน" sub="ที่นั่งที่มีเจ้าของแล้ว">
              <ul className="jt-order-lines">
                {occupancy.filter((o) => o.orderCode).map((o) => (
                  <li key={o.seatCode}>
                    <span className="jt-mono">{o.seatCode}</span>
                    <span className="jt-mono">{o.orderCode}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      }
    />
  );
}

export function SeatPlanDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useSeatPlan(token, eventSlug);
  return <SeatPlanScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
